import React, { useState, useEffect } from 'react';
import { University, UserProfile } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Building, Plus, Users, Mail, Settings, Trash2, Edit } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

export default function UniversityManagement() {
  const [universities, setUniversities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [newUniversity, setNewUniversity] = useState({
    name: '',
    domain: '',
    settings: {}
  });
  const [inviteData, setInviteData] = useState({
    email: '',
    fullName: ''
  });
  const { user, profile } = useAuth();

  useEffect(() => {
    loadUniversities();
  }, []);

  const loadUniversities = async () => {
    setIsLoading(true);
    try {
      const data = await University.list();
      setUniversities(data);
    } catch (error) {
      console.error('Error loading universities:', error);
    }
    setIsLoading(false);
  };

  const handleCreateUniversity = async (e) => {
    e.preventDefault();
    try {
      await University.create({
        ...newUniversity,
        settings: {
          commission_default_rate: 15,
          auto_approve_agents: false,
          require_document_approval: true,
          ...newUniversity.settings
        }
      });
      
      setNewUniversity({ name: '', domain: '', settings: {} });
      setIsAddModalOpen(false);
      loadUniversities();
      toast({
        title: 'Success',
        description: 'University created successfully',
      });
    } catch (error) {
      console.error('Error creating university:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create university. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();

    if (!selectedUniversity) {
      toast({
        title: 'Error',
        description: 'Please select a university first',
        variant: 'destructive',
      });
      return;
    }

    if (!profile || !profile.id) {
      toast({
        title: 'Error',
        description: 'User profile not loaded. Please refresh the page.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmittingInvite(true);
    try {
      const invitationData = {
        email: inviteData.email.toLowerCase().trim(),
        role: 'university_admin',
        university_id: selectedUniversity.id,
        invited_by: profile.id,
        metadata: { full_name: inviteData.fullName }
      };

      const { data, error } = await supabase
        .from('invitations')
        .insert(invitationData)
        .select();

      if (error) throw error;

      setInviteData({ email: '', fullName: '' });
      setIsInviteModalOpen(false);
      setSelectedUniversity(null);
      toast({
        title: 'Success',
        description: `Invitation sent to ${inviteData.email}`,
      });
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send invitation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingInvite(false);
    }
  };

  const handleStatusChange = async (universityId, newStatus) => {
    try {
      await University.update(universityId, { status: newStatus });
      loadUniversities();
      toast({
        title: 'Success',
        description: `University status updated to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating university status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update status. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-8 w-1/3 mb-8" />
          <div className="grid gap-6">
            {Array(3).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-3xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-neumorphic mb-2">University Management</h1>
            <p className="text-gray-600">Manage university organizations and their administrators</p>
          </div>
          <Button 
            onClick={() => setIsAddModalOpen(true)}
            className="neumorphic rounded-xl px-4 py-2 flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
          >
            <Plus className="w-5 h-5" />
            Add University
          </Button>
        </div>

        <div className="grid gap-6">
          {universities.map(university => (
            <Card key={university.id} className="neumorphic rounded-3xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 neumorphic rounded-2xl flex items-center justify-center">
                      <Building className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-neumorphic">{university.name}</h3>
                      <p className="text-gray-600">{university.domain}</p>
                      <p className="text-sm text-gray-500">
                        Created: {new Date(university.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Badge className={`${
                      university.status === 'active' ? 'bg-green-100 text-green-800' :
                      university.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    } border-0 capitalize px-3 py-1`}>
                      {university.status}
                    </Badge>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedUniversity(university);
                          setIsInviteModalOpen(true);
                        }}
                        className="neumorphic rounded-xl px-3 py-2"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Invite Admin
                      </Button>
                      
                      {university.status === 'active' ? (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(university.id, 'suspended')}
                          className="neumorphic rounded-xl px-3 py-2 text-red-600"
                        >
                          Suspend
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(university.id, 'active')}
                          className="neumorphic rounded-xl px-3 py-2 text-green-600"
                        >
                          Activate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add University Modal */}
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-indigo-600" />
                Add New University
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreateUniversity} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="uni-name">University Name</Label>
                <Input
                  id="uni-name"
                  value={newUniversity.name}
                  onChange={(e) => setNewUniversity({...newUniversity, name: e.target.value})}
                  placeholder="Demo University"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="uni-domain">Domain</Label>
                <Input
                  id="uni-domain"
                  value={newUniversity.domain}
                  onChange={(e) => setNewUniversity({...newUniversity, domain: e.target.value})}
                  placeholder="demo.edu"
                  required
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Create University
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Invite Admin Modal */}
        <Dialog open={isInviteModalOpen} onOpenChange={setIsInviteModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Invite University Administrator
              </DialogTitle>
            </DialogHeader>
            
            {selectedUniversity && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>University:</strong> {selectedUniversity.name}
                </p>
              </div>
            )}
            
            <form onSubmit={handleInviteAdmin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                  placeholder="admin@university.edu"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-name">Full Name</Label>
                <Input
                  id="admin-name"
                  value={inviteData.fullName}
                  onChange={(e) => setInviteData({...inviteData, fullName: e.target.value})}
                  placeholder="Administrator Name"
                  required
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsInviteModalOpen(false)}
                  disabled={isSubmittingInvite}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingInvite}
                >
                  {isSubmittingInvite ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}